<template>
  <div class="story-practice-page">
    <div id="iRecorder" class="recorder-host"></div>

    <section v-if="loading" class="center-state">
      <p>加载中...</p>
    </section>

    <section v-else-if="loadError" class="center-state">
      <p>{{ loadError }}</p>
      <button class="pill-btn" @click="initPage">重试</button>
    </section>

    <transition name="scene-switch" mode="out-in">
      <section
        v-if="mode === 'practice'"
        :key="practiceScreen"
        class="practice-stage"
      >
        <button
          class="skip-segment-btn"
          :disabled="recordStatus === 'recording' || recordStatus === 'scoring'"
          @click="handleTopBack"
        >
          {{ topBackLabel }}
        </button>

        <div class="lesson-badge">Lesson {{ segmentIndex + 1 }}</div>

        <template v-if="practiceScreen === 'sentence'">
          <div class="page-title">英语纠音·句子</div>

          <main class="practice-main sentence-main">
            <p class="hint-text">请先听示范，再朗读以下句子</p>
            <div class="sentence-text" aria-label="当前朗读句子">
              {{ currentLine.text }}
            </div>

            <button
              v-if="currentLineAudioUrl"
              class="play-ref-btn"
              type="button"
              @click="playReferenceAudio(currentLineAudioUrl)"
            >
              听示范
            </button>

            <div
              v-if="recordStatus === 'done' && currentResult"
              class="line-result"
            >
              <p class="score-label">本句得分</p>
              <p class="score-number">
                {{ currentResult.overall }}<span>分</span>
              </p>
            </div>
          </main>

          <footer class="practice-footer">
            <button
              class="mic-btn"
              :class="{ recording: recordStatus === 'recording' }"
              :disabled="recordStatus === 'scoring'"
              @click="handleRecordClick"
            ></button>

            <p class="record-hint">{{ recordHint }}</p>
            <p v-if="!recorderReady" class="recorder-status">
              {{ recorderMessage }}
            </p>

            <div v-if="recordStatus === 'done'" class="line-actions">
              <button class="pill-btn light" @click="retryLine">
                再试一次
              </button>
              <button class="pill-btn" @click="goNextLine">继续</button>
            </div>
          </footer>
        </template>

        <template v-else-if="practiceScreen === 'sentenceResult'">
          <main class="sentence-result-stage">
            <section class="sentence-score-panel" v-if="currentResult">
              <div
                class="score-ring"
                :style="{
                  background: scoreRingBackground(currentResult.overall),
                }"
              >
                <div class="score-ring-inner">
                  <strong>{{ currentResult.overall }}</strong
                  ><span>分</span>
                  <p>总分</p>
                </div>
              </div>

              <div class="score-breakdown">
                <div
                  v-for="item in sentenceScoreItems"
                  :key="item.key"
                  class="score-meter"
                >
                  <span class="meter-label">{{ item.label }}</span>
                  <div class="meter-track">
                    <i :style="{ width: item.percent + '%' }"></i>
                  </div>
                  <strong>{{ item.value }}</strong
                  ><span class="meter-unit">%</span>
                </div>

                <div class="speed-meter">
                  <span class="meter-label">语速</span>
                  <strong>{{ sentenceSpeed }}</strong>
                  <span class="speed-unit">词/分钟</span>
                </div>
              </div>
            </section>

            <section class="sentence-word-panel">
              <p>点击单词, 查看单词和音素发音分</p>
              <div class="result-word-list">
                <button
                  v-for="(word, index) in currentLineWords"
                  :key="`${word.text}-${index}`"
                  class="result-word-chip"
                  @click="openSentenceWordResult(index)"
                >
                  {{ word.text }}
                </button>
              </div>
            </section>

            <div class="sentence-result-actions">
              <button class="pill-btn wide" @click="retryLine">再试一次</button>
              <button class="pill-btn wide" @click="goNextLine">继续</button>
            </div>
          </main>
        </template>

        <template v-else-if="practiceScreen === 'wordRecord'">
          <div class="page-title">英语纠音·单词</div>

          <main class="practice-main word-main">
            <p class="hint-text">请先听示范，再朗读以下单词</p>
            <h1 class="target-word">{{ currentWord.text }}</h1>
            <p v-if="currentWord.phonetic" class="word-phonetic-hint">{{ currentWord.phonetic }}</p>
            <button
              v-if="currentWordAudioUrl"
              class="play-ref-btn"
              type="button"
              @click="playReferenceAudio(currentWordAudioUrl)"
            >
              听示范
            </button>
          </main>

          <footer class="practice-footer">
            <button v-if="!requiredWordMode" class="swap-btn" @click="changeWord">
              <img class="swap-icon" src="../assets/swap-word.png" alt="" />
              <span>换个词</span>
            </button>

            <button
              class="mic-btn"
              :class="{ recording: recordStatus === 'recording' }"
              :disabled="recordStatus === 'scoring'"
              @click="handleRecordClick"
            ></button>

            <p class="record-hint">{{ recordHint }}</p>
            <p v-if="!recorderReady" class="recorder-status">
              {{ recorderMessage }}
            </p>
          </footer>
        </template>

        <template v-else>
          <main class="word-result-stage">
            <button class="play-record-btn" @click="playWordRecording">
              <span class="play-record-icon"></span>
              <span>播放录音</span>
            </button>

            <div class="summary-score word-score">
              <p>总分</p>
              <strong>{{ currentWordResult.overall }}</strong
              ><span>分</span>
            </div>

            <section class="word-result-hero">
              <h1 :class="{ weak: hasCurrentWordError }">
                {{ currentWord.text }}
              </h1>
              <p
                v-if="currentWordPhoneticSegments.length"
                class="word-phonetic-text"
              >
                <span class="phonetic-slash">/</span><span
                  v-for="(phoneme, index) in currentWordPhoneticSegments"
                  :key="`${phoneme.text}-${index}`"
                  :class="phoneme.scoreClass"
                >{{ phoneme.text }}</span><span class="phonetic-slash">/</span>
              </p>
              <div class="error-tabs">
                <button
                  :class="{ active: wordResultTab === 'error' }"
                  @click="wordResultTab = 'error'"
                >
                  发音偏误
                </button>
                <button
                  :class="{ active: wordResultTab === 'score' }"
                  @click="wordResultTab = 'score'"
                >
                  评分结果
                </button>
              </div>
            </section>

            <div class="phoneme-table">
              <div class="phoneme-row header">
                <span>音标</span>
                <span>实际发音</span>
                <span>{{ wordResultTab === "score" ? "评分结果" : "发音判断" }}</span>
              </div>
              <div
                v-for="(phoneme, index) in currentWordResult.phonemes"
                :key="`${phoneme.expected}-${index}`"
                class="phoneme-row"
                :class="{ weak: wordResultTab === 'error' && !phoneme.correct }"
              >
                <span>{{ phoneme.expected }}</span>
                <span>{{ phoneme.actual }}</span>
                <strong v-if="wordResultTab === 'score'">
                  {{ phoneme.pronunciation }}
                </strong>
                <strong v-else>{{ phoneme.statusText }}</strong>
              </div>
            </div>

            <div class="word-result-actions">
              <button class="pill-btn wide" @click="retryWord">再试一次</button>
              <button v-if="!requiredWordMode" class="pill-btn wide" @click="changeWord">换个词</button>
              <button v-if="requiredWordMode" class="pill-btn wide" @click="goNextRequiredWord">继续</button>
            </div>
          </main>
        </template>
      </section>

      <section v-else key="summary" class="summary-stage">
        <div class="summary-score">
          <p>{{ lessonGrade ? "等级" : "总分" }}</p>
          <strong>{{ lessonGrade || totalScore }}</strong
          ><span v-if="!lessonGrade">分</span>
        </div>

        <div class="summary-table">
          <template v-if="summaryWordItems.length">
            <div class="summary-row header">
              <span>单词</span>
              <span>得分</span>
              <span>录音</span>
            </div>
            <div
              v-for="result in summaryWordItems"
              :key="result.key"
              class="summary-row"
              :class="{ weak: result.overall < 70 }"
            >
              <span>{{ result.text }}</span>
              <span>{{ result.overall }}</span>
              <button @click="replayRecord(result.recordId)">播放</button>
            </div>
          </template>
          <div class="summary-row header">
            <span>句子</span>
            <span>得分</span>
            <span>录音</span>
          </div>
          <div
            v-for="result in results"
            :key="result.lineId"
            class="summary-row"
            :class="{ weak: result.overall < 70 }"
          >
            <span>{{ result.text }}</span>
            <span>{{ result.overall }}</span>
            <button @click="replayRecord(result.recordId)">播放</button>
          </div>
        </div>

        <div class="summary-actions">
          <button class="pill-btn light" @click="restartPractice">
            再练一次
          </button>
          <button v-if="hasNextLesson" class="pill-btn" @click="goNextLesson">
            下一课
          </button>
          <button class="pill-btn" @click="backToLessonSelect">
            返回选课
          </button>
        </div>
      </section>
    </transition>
  </div>
</template>

<script>
import { _17kouyu } from "@/components/iload_es";
import { completeActivityLesson, getActivityLessonPractice } from "@/api/activity";
import { ensureStudentSession } from "@/utils/auth";
import { resolveActivityId } from "@/utils/activity-route";

const Config = {
  appKey: "17769972980006ff",
  secretKey: "2f5b9a61269e1ec717a331cf8a8846eb",
};

export default {
  name: "StoryPractice",
  data() {
    return {
      loading: true,
      loadError: "",
      activityId: null,
      bookId: null,
      story: null,
      user: null,
      mode: "practice",
      practiceScreen: "sentence",
      activeRecordKind: "sentence",
      requiredWordMode: false,
      requiredWordIndex: 0,
      requiredWords: [],
      lessonGrade: "",
      segmentIndex: 0,
      currentLineIndex: 0,
      selectedWordIndex: 0,
      sessionId: "",
      startedAt: "",
      results: [],
      wordResults: [],
      recorder: null,
      lastRecordId: "",
      recordStatus: "idle",
      recordTimer: null,
      recorderReady: false,
      recorderMessage: "录音机初始化中...",
      recorderReadyTimer: null,
      recordSeconds: 0,
      wordAudioPlayer: null,
      wordResultTab: "error",
      savingResult: false,
    };
  },
  computed: {
    currentSegment() {
      if (!this.story || !this.story.storySegments) {
        return { lines: [] };
      }
      return (
        this.story.storySegments[this.segmentIndex] || {
          lines: [],
        }
      );
    },
    currentLines() {
      return this.currentSegment.lines || [];
    },
    currentLine() {
      return this.currentLines[this.currentLineIndex] || { text: "", audioUrl: "" };
    },
    currentLineAudioUrl() {
      return String(this.currentLine.audioUrl || "").trim();
    },
    currentLineWords() {
      return this.extractWords(this.currentLine.text);
    },
    currentWord() {
      if (this.requiredWordMode) {
        const word = this.requiredWords[this.requiredWordIndex] || {};
        const text = word.word || "";
        return {
          text,
          normalized: String(text).toLowerCase(),
          wordId: word.id,
          phonetic: word.phonetic || "",
          audioUrl: word.audioUrl || "",
        };
      }
      return (
        this.currentLineWords[this.selectedWordIndex] || {
          text: "",
          normalized: "",
          phonetic: "",
          audioUrl: "",
        }
      );
    },
    currentWordAudioUrl() {
      return String(this.currentWord.audioUrl || "").trim();
    },
    currentWordPhonetic() {
      return this.currentWordResult.phonetic || "";
    },
    currentWordPhoneticSegments() {
      const phonemes = this.currentWordResult.phonemes || [];
      const segments = phonemes
        .map((item) => ({
          text: this.unwrapPhoneme(item.expected),
          scoreClass: this.getPhonemeScoreClass(item.pronunciation),
        }))
        .filter((item) => item.text);

      if (segments.length) return segments;
      return this.unwrapPhoneme(this.currentWordPhonetic)
        .split("")
        .filter(Boolean)
        .map((text) => ({
          text,
          scoreClass: "",
        }));
    },
    hasCurrentWordError() {
      return this.currentWordResult.phonemes.some((item) => !item.correct);
    },
    currentResult() {
      return this.results.find(
        (result) => result.lineId === this.currentLine.id
      );
    },
    currentWordResult() {
      return (
        this.wordResults.find(
          (result) => result.key === this.currentWordKey
        ) || {
          overall: 0,
          phonetic: "",
          phonemes: this.buildFallbackPhonemes(
            this.currentWord.normalized,
            true
          ),
        }
      );
    },
    currentWordKey() {
      if (this.requiredWordMode) {
        const word = this.requiredWords[this.requiredWordIndex] || {};
        return `required:${word.id || this.requiredWordIndex}`;
      }
      return `${this.currentLine.id}:${this.currentWord.normalized}`;
    },
    summaryWordItems() {
      return this.requiredWords
        .map((word, index) => {
          const key = `required:${word.id || index}`;
          const result = this.wordResults.find((item) => item.key === key);
          return result
            ? {
                key,
                text: word.word || result.text,
                overall: result.overall,
                recordId: result.recordId,
              }
            : null;
        })
        .filter(Boolean);
    },
    isLastLineInSegment() {
      return this.currentLineIndex >= this.currentLines.length - 1;
    },
    hasRequiredWords() {
      return this.requiredWords.length > 0;
    },
    isLastRequiredWord() {
      return this.requiredWordIndex >= this.requiredWords.length - 1;
    },
    hasSentences() {
      return this.currentLines.length > 0;
    },
    hasNextLesson() {
      if (!this.story || !Array.isArray(this.story.storySegments)) return false;
      const nextIndex = this.segmentIndex + 1;
      if (nextIndex >= this.story.storySegments.length) return false;
      const nextSegment = this.story.storySegments[nextIndex];
      return Array.isArray(nextSegment.lines) && nextSegment.lines.length > 0;
    },
    totalScore() {
      if (!this.results.length) return 0;
      const sum = this.results.reduce((total, item) => total + item.overall, 0);
      return Math.round(sum / this.results.length);
    },
    recordHint() {
      if (this.recordStatus === "recording")
        return `正在录音... ${this.recordSeconds}s`;
      if (this.recordStatus === "scoring") return "正在评测中...";
      if (this.recordStatus === "done") return "评测完成";
      if (!this.recorderReady) return "录音机准备中";
      return "点击麦克风 开始录音";
    },
    sentenceScoreItems() {
      if (!this.currentResult) return [];
      return [
        {
          key: "pronunciation",
          label: "发音",
          value: this.currentResult.pronunciation,
          percent: this.toPercent(this.currentResult.pronunciation),
        },
        {
          key: "fluency",
          label: "流畅",
          value: this.currentResult.fluency,
          percent: this.toPercent(this.currentResult.fluency),
        },
        {
          key: "integrity",
          label: "完整",
          value: this.currentResult.integrity,
          percent: this.toPercent(this.currentResult.integrity),
        },
      ];
    },
    sentenceSpeed() {
      if (!this.currentResult) return "000";
      return this.formatSpeed(this.currentResult.speed);
    },
    showSentenceBack() {
      return (
        !this.requiredWordMode &&
        (this.practiceScreen === "wordRecord" ||
          this.practiceScreen === "wordResult")
      );
    },
    topBackLabel() {
      return this.showSentenceBack ? "返回句子" : "返回";
    },
  },
  created() {
    this.activityId = resolveActivityId(this.$route);
    this.bookId = this.$route.params.bookId;
    this.sessionId = `story-session-${Date.now()}`;
    this.startedAt = new Date().toISOString();
    this.initPage();
  },
  watch: {
    "$route.params.lessonIndex"() {
      this.sessionId = `story-session-${Date.now()}`;
      this.startedAt = new Date().toISOString();
      this.initPage();
    },
  },
  mounted() {
    this.initRecorder();
  },
  beforeDestroy() {
    this.clearRecordTimer();
    this.clearRecorderReadyTimer();
    if (this.recorder) {
      this.recorder = null;
    }
    this.stopWordAudio();
  },
  methods: {
    async initPage() {
      this.loading = true;
      this.loadError = "";
      this.requiredWordMode = false;
      this.requiredWordIndex = 0;
      this.lessonGrade = "";

      const lessonIndex = Number(this.$route.params.lessonIndex);
      if (!Number.isFinite(lessonIndex) || lessonIndex < 0) {
        this.$router.replace(`/a/${this.activityId}/lessons`);
        return;
      }

      try {
        const session = ensureStudentSession(this.activityId);
        if (!session) {
          this.$router.replace(`/a/${this.activityId}`);
          return;
        }
        this.user = session;
        const res = await getActivityLessonPractice(this.activityId, lessonIndex);
        const lesson = res.data;
        this.bookId = lesson.bookId;
        this.requiredWords = lesson.words || [];
        this.story = {
          title: lesson.bookTitle,
          storySegments: [
            {
              id: lesson.lessonId,
              lines: lesson.lines || [],
            },
          ],
        };

        if (!this.currentLines.length && !this.requiredWords.length) {
          this.loadError = "该 Lesson 暂无测评内容";
          return;
        }

        this.segmentIndex = 0;
        this.mode = "practice";
        this.results = [];
        this.wordResults = [];
        this.currentLineIndex = 0;
        this.selectedWordIndex = 0;
        if (this.hasRequiredWords) {
          this.startRequiredWords();
        } else {
          this.startSentences();
        }
      } catch (error) {
        this.loadError = error.message || "加载失败，请重试";
      } finally {
        this.loading = false;
      }
    },
    initRecorder() {
      if (this.recorder) return;

      try {
        this.recorder = new _17kouyu.IRecorder({
          id: "iRecorder",
          mode: 2,
          debug: true,
          appKey: Config.appKey,
          secretKey: Config.secretKey,
          onFlashLoad: (code) => {
            if (String(code) === "50000") {
              this.recorderMessage = "录音机加载完成，正在连接服务...";
            }
            this.checkRecorderReady();
          },
          onConnectorStatusChange: (code) => {
            if (String(code) === "50100") {
              this.recorderMessage = "录音服务已连接";
              this.checkRecorderReady();
            } else if (String(code) === "50103" || String(code) === "50104") {
              this.recorderMessage = "录音服务连接失败，请检查网络";
            } else if (String(code) === "50109") {
              this.recorderMessage = "正在连接录音服务...";
            }
          },
          onMicStatusChange: (code) => {
            if (String(code) === "50001") {
              this.recorderMessage = "麦克风已授权";
              this.checkRecorderReady();
            } else if (String(code) === "50002") {
              this.recorderReady = false;
              this.recorderMessage = "麦克风未授权，请允许浏览器访问麦克风";
            } else if (String(code) === "50003") {
              this.recorderReady = false;
              this.recorderMessage = "未检测到麦克风设备";
            }
          },
          onScore: (message) => {
            this.handleScoreResult(message);
          },
          onError: (message) => {
            console.error(message);
            this.recorderReady = false;
            this.recorderMessage = this.formatRecorderError(message);
            this.clearRecordTimer();
            this.recordStatus = "idle";
            alert("录音评测失败，请重试");
          },
        });
        this.startRecorderReadyPolling();
      } catch (error) {
        console.error("初始化录音失败:", error);
        this.recorderReady = false;
        this.recorderMessage = "录音机初始化失败";
      }
    },
    startRecorderReadyPolling() {
      this.clearRecorderReadyTimer();
      let attempts = 0;
      this.recorderReadyTimer = setInterval(() => {
        attempts += 1;
        if (this.checkRecorderReady() || attempts >= 40) {
          this.clearRecorderReadyTimer();
          if (!this.recorderReady) {
            this.recorderMessage =
              this.recorderMessage || "录音机未准备好，请检查麦克风权限";
          }
        }
      }, 500);
    },
    checkRecorderReady() {
      if (this.recorder && this.recorder.canRecord) {
        this.recorderReady = true;
        this.recorderMessage = "";
        return true;
      }
      return false;
    },
    clearRecorderReadyTimer() {
      if (this.recorderReadyTimer) {
        clearInterval(this.recorderReadyTimer);
        this.recorderReadyTimer = null;
      }
    },
    formatRecorderError(message) {
      const text = typeof message === "string" ? message : "";
      if (text.indexOf("no audio device") > -1) {
        return "无法访问麦克风，请检查浏览器权限，并确认页面通过 HTTPS 打开";
      }
      if (text.indexOf("no audio context") > -1) {
        return "当前浏览器不支持 H5 录音";
      }
      if (text.indexOf("no audio media") > -1) {
        return "当前浏览器无法获取麦克风媒体流";
      }
      return text || "录音机初始化失败";
    },
    handleRecordClick() {
      if (this.recordStatus === "recording") {
        this.stopRecord();
        return;
      }

      if (!this.recorderReady) {
        this.requestRecorderAccess(true);
        return;
      }

      if (this.recordStatus === "idle" || this.recordStatus === "done") {
        this.startRecord();
      }
    },
    requestRecorderAccess(startAfterReady) {
      if (!this.recorder || typeof this.recorder.active !== "function") {
        this.recorderReady = false;
        this.recorderMessage =
          "当前浏览器不支持 H5 录音，请在微信内或支持麦克风的浏览器中打开";
        return;
      }

      this.recorderMessage = "正在请求麦克风权限...";
      this.recorder.active({
        onSuccess: () => {
          this.recorderReady = true;
          this.recorderMessage = "";
          if (startAfterReady) {
            this.startRecord();
          }
        },
        onError: (message) => {
          this.recorderReady = false;
          this.recordStatus = "idle";
          this.recorderMessage =
            message || "麦克风权限获取失败，请检查浏览器权限";
        },
      });
    },
    startRecord() {
      this.stopWordAudio();
      this.lastRecordId = "";
      this.recordStatus = "recording";
      this.recordSeconds = 0;
      this.activeRecordKind =
        this.practiceScreen === "wordRecord" ? "word" : "sentence";
      this.startRecordTimer();

      if (!this.recorder || !this.recorder.canRecord) {
        this.clearRecordTimer();
        this.recordStatus = "idle";
        this.recorderReady = false;
        this.recorderMessage =
          this.recorderMessage || "录音机尚未准备好，请检查麦克风权限";
        return;
      }

      const isWord = this.activeRecordKind === "word";
      this.recorder.record({
        duration: isWord ? 5000 : 10000,
        serverParams: {
          coreType: isWord ? "word.eval.promax" : "sent.eval",
          refText: isWord ? this.currentWord.normalized : this.currentLine.text,
          userId: this.user.openid || "guest",
          attachAudioUrl: 1,
        },
        onRecordIdGenerated: (code, message) => {
          this.lastRecordId = message.recordId;
        },
        onStop: () => {
          this.recordStatus = "scoring";
          this.clearRecordTimer();
        },
      });
    },
    stopRecord() {
      this.recordStatus = "scoring";
      this.clearRecordTimer();

      if (!this.recorder || !this.recorder.canRecord) {
        this.recordStatus = "idle";
        alert("录音机尚未准备好，请稍后再试");
        return;
      }

      this.recorder.stop({
        onStop: () => {
          this.recordStatus = "scoring";
        },
      });
    },
    startRecordTimer() {
      this.clearRecordTimer();
      this.recordTimer = setInterval(() => {
        this.recordSeconds += 1;
        const maxSeconds = this.activeRecordKind === "word" ? 5 : 10;
        if (this.recordSeconds >= maxSeconds) {
          this.stopRecord();
        }
      }, 1000);
    },
    clearRecordTimer() {
      if (this.recordTimer) {
        clearInterval(this.recordTimer);
        this.recordTimer = null;
      }
    },
    async handleScoreResult(message) {
      if (this.savingResult) return;
      this.savingResult = true;
      this.recordStatus = "scoring";
      this.clearRecordTimer();

      try {
        const messageObj =
          typeof message === "string" ? JSON.parse(message) : message;
        if (!messageObj || !messageObj.result || !this.lastRecordId) {
          throw new Error("未获取到有效评分结果");
        }

        if (this.activeRecordKind === "word") {
          await this.handleWordScoreResult(messageObj);
        } else {
          await this.handleSentenceScoreResult(messageObj);
        }
      } catch (error) {
        this.recordStatus = "idle";
        alert(error.message || "评分失败，请重试");
      } finally {
        this.savingResult = false;
      }
    },
    handleSentenceScoreResult(messageObj) {
      const sdkResult = messageObj.result || {};
      const overall = this.readScore(sdkResult.overall);
      const result = {
        sessionId: this.sessionId,
        userId: this.user.userId,
        openid: this.user.openid,
        bookId: Number(this.bookId),
        segmentId: this.currentSegment.id,
        lineId: this.currentLine.id,
        text: this.currentLine.text,
        recordId: this.lastRecordId,
        overall,
        pronunciation: this.readScore(sdkResult.pronunciation),
        fluency: this.readScore(sdkResult.fluency),
        integrity: this.readScore(sdkResult.integrity),
        speed: this.readSpeed(sdkResult.speed),
        words: this.buildWordScores(overall),
        phonemes: [],
        raw: messageObj || {},
      };

      this.upsertResult(result);
      this.upsertSentenceWordResults(messageObj, result);
      this.recordStatus = "done";
      this.practiceScreen = "sentenceResult";
    },
    handleWordScoreResult(messageObj) {
      const sdkResult = messageObj.result || {};
      const sdkWord = this.getResultWords(messageObj)[0] || {};
      const scores = sdkWord.scores || {};
      const overall = this.pickScore(
        sdkResult.overall,
        scores.overall,
        scores.pronunciation,
        sdkWord.overall,
        sdkWord.pronunciation
      );
      const result = {
        key: this.currentWordKey,
        lineId: this.currentLine.id,
        text: this.currentWord.text,
        normalized: this.currentWord.normalized,
        recordId: this.lastRecordId,
        audioUrl: messageObj.audioUrl || "",
        overall,
        phonetic: this.extractWordPhonetic(messageObj),
        phonemes: this.parseWordPhonemes(messageObj, overall),
        raw: messageObj || {},
      };

      this.upsertWordResult(result);
      this.recordStatus = "done";
      this.practiceScreen = "wordResult";
      this.wordResultTab = "error";
    },
    buildWordScores(overall) {
      return this.currentLineWords.map((word) => ({
        text: word.normalized,
        score: Math.max(
          50,
          Math.min(100, overall + Math.floor(Math.random() * 9) - 4)
        ),
      }));
    },
    extractWords(text) {
      return String(text || "")
        .split(/\s+/)
        .filter(Boolean)
        .map((token) => {
          const normalized = token
            .toLowerCase()
            .replace(/^[^a-z]+|[^a-z]+$/g, "");
          return {
            text: token,
            normalized,
          };
        })
        .filter((word) => word.normalized);
    },
    parseWordPhonemes(messageObj, overall) {
      const phonemes = this.getResultPhonemes(messageObj);
      if (phonemes.length) {
        return phonemes
          .map((item) => {
            const readType = this.normalizeReadType(item.readType);
            return {
              expected: this.wrapPhoneme(item.phoneme || ""),
              actual: this.wrapPhoneme(item.sound_like || item.phoneme || ""),
              pronunciation: this.readScore(item.pronunciation),
              readType,
              statusText: this.getPhonemeStatusText(readType),
              correct: readType === "0",
            };
          })
          .filter((item) => item.expected || item.actual);
      }

      const phonics = this.getResultPhonics(messageObj);
      if (phonics.length) {
        return phonics
          .map((item) => {
            if (typeof item === "string") {
              return {
                expected: this.wrapPhoneme(item),
                actual: this.wrapPhoneme(item),
                pronunciation: this.readScore(overall),
                readType: "0",
                statusText: "一致",
                correct: true,
              };
            }

            const expected =
              item.phonic ||
              item.phoneme ||
              item.phone ||
              item.text ||
              item.char ||
              item.symbol ||
              "";
            const actual =
              item.userPhonic ||
              item.userPhone ||
              item.actual ||
              item.read ||
              expected;
            const score = Number(item.score || item.overall || overall);
            return {
              expected: this.wrapPhoneme(expected),
              actual: this.wrapPhoneme(actual),
              pronunciation: this.readScore(score),
              readType: score >= 60 ? "0" : "6",
              statusText: score >= 60 ? "一致" : "混淆",
              correct: score >= 60,
            };
          })
          .filter((item) => item.expected);
      }

      const details =
        messageObj.result &&
        (messageObj.result.details || messageObj.result.phonemes);
      if (Array.isArray(details) && details.length) {
        return details
          .map((item) => {
            const expected =
              item.phoneme || item.phone || item.text || item.char || "";
            const actual = item.userPhone || item.actual || expected;
            const score = Number(item.score || item.overall || overall);
            return {
              expected: this.wrapPhoneme(expected),
              actual: this.wrapPhoneme(actual),
              pronunciation: this.readScore(score),
              readType: score >= 60 ? "0" : "6",
              statusText: score >= 60 ? "一致" : "混淆",
              correct: score >= 60,
            };
          })
          .filter((item) => item.expected);
      }
      return this.buildFallbackPhonemes(
        this.currentWord.normalized,
        overall >= 60
      );
    },
    getResultPhonics(messageObj) {
      const words = messageObj && messageObj.result && messageObj.result.words;
      if (!words) return [];

      const phonics = Array.isArray(words)
        ? words[0] && words[0].phonics
        : words.phonics;

      if (Array.isArray(phonics)) return phonics;
      if (typeof phonics === "string") {
        return phonics
          .replace(/^\/|\/$/g, "")
          .split(/\s+/)
          .filter(Boolean);
      }
      return [];
    },
    getResultPhonemes(messageObj) {
      const words = messageObj && messageObj.result && messageObj.result.words;
      if (!words) return [];

      const word = Array.isArray(words) ? words[0] : words;
      if (word && Array.isArray(word.phonemes)) return word.phonemes;
      return [];
    },
    normalizeReadType(value) {
      if (value === 1 / 2) return "1/2";
      return String(value);
    },
    getPhonemeStatusText(readType) {
      if (readType === "0") return "一致";
      if (readType === "1" || readType === "1/2" || readType === "0.5") {
        return "多读";
      }
      if (readType === "3") return "漏读";
      if (readType === "6") return "混淆";
      return "偏误";
    },
    extractWordPhonetic(messageObj) {
      const phonics = this.getResultPhonics(messageObj);
      if (!phonics.length) {
        const phonemes = this.getResultPhonemes(messageObj);
        const text = phonemes
          .map((item) => item.phoneme || "")
          .filter(Boolean)
          .map((item) => String(item).replace(/^\/|\/$/g, ""))
          .join("");
        return text ? `/${text}/` : "";
      }

      const text = phonics
        .map((item) => {
          if (typeof item === "string") return item;
          return (
            item.phonic ||
            item.phoneme ||
            item.phone ||
            item.text ||
            item.char ||
            item.symbol ||
            ""
          );
        })
        .filter(Boolean)
        .map((item) => String(item).replace(/^\/|\/$/g, ""))
        .join("");

      return text ? `/${text}/` : "";
    },
    buildFallbackPhonemes(word, correct) {
      const source =
        word === "fast" ? ["f", "æ", "s", "t"] : String(word || "").split("");
      return source.map((item) => ({
        expected: this.wrapPhoneme(item),
        actual: this.wrapPhoneme(item),
        pronunciation: correct ? 100 : 0,
        readType: correct ? "0" : "6",
        statusText: correct ? "一致" : "混淆",
        correct,
      }));
    },
    wrapPhoneme(value) {
      const text = String(value || "").replace(/^\/|\/$/g, "");
      return text ? `/${text}/` : "";
    },
    unwrapPhoneme(value) {
      return String(value || "").replace(/^\/|\/$/g, "");
    },
    getPhonemeScoreClass(score) {
      const value = Number(score || 0);
      if (!isFinite(value)) return "";
      if (value > 80) return "phonetic-score-high";
      if (value < 60) return "phonetic-score-low";
      return "";
    },
    pickScore() {
      for (let i = 0; i < arguments.length; i += 1) {
        const value = arguments[i];
        if (value === 0 || value === "0") return this.readScore(value);
        if (value !== undefined && value !== null && value !== "") {
          return this.readScore(value);
        }
      }
      return 0;
    },
    readScore(value) {
      const score = Number(value || 0);
      if (!isFinite(score)) return 0;
      return Math.max(0, Math.min(100, Math.round(score)));
    },
    readSpeed(value) {
      const speed = Number(value || 0);
      if (!isFinite(speed)) return 0;
      return Math.max(0, Math.round(speed));
    },
    toPercent(value) {
      const score = Number(value || 0);
      if (!isFinite(score)) return 0;
      return Math.max(0, Math.min(100, score));
    },
    formatSpeed(value) {
      const text = String(this.readSpeed(value));
      if (text.length >= 3) return text;
      return new Array(4 - text.length).join("0") + text;
    },
    scoreRingBackground(value) {
      const percent = this.toPercent(value);
      return `conic-gradient(#7df28c 0 ${percent * 3.6}deg, transparent ${
        percent * 3.6
      }deg 360deg)`;
    },
    hasWordResult(word) {
      return this.wordResults.some(
        (result) =>
          result.lineId === this.currentLine.id && result.normalized === word
      );
    },
    upsertResult(result) {
      const index = this.results.findIndex(
        (item) => item.lineId === result.lineId
      );
      if (index >= 0) {
        this.results.splice(index, 1, result);
      } else {
        this.results.push(result);
      }
    },
    upsertWordResult(result) {
      const index = this.wordResults.findIndex(
        (item) => item.key === result.key
      );
      if (index >= 0) {
        this.wordResults.splice(index, 1, result);
      } else {
        this.wordResults.push(result);
      }
    },
    upsertSentenceWordResults(messageObj, lineResult) {
      const sdkWords = this.getResultWords(messageObj);
      this.currentLineWords.forEach((word, index) => {
        const sdkWord = this.findSdkWordResult(sdkWords, word, index);
        const scores = sdkWord.scores || {};
        const overall = this.pickScore(
          scores.overall,
          scores.pronunciation,
          sdkWord.overall,
          sdkWord.pronunciation,
          lineResult.overall
        );
        const wordMessage = {
          audioUrl: messageObj.audioUrl || "",
          result: {
            overall,
            words: sdkWord,
          },
        };
        const result = {
          key: `${this.currentLine.id}:${word.normalized}`,
          lineId: this.currentLine.id,
          text: word.text,
          normalized: word.normalized,
          recordId: lineResult.recordId,
          audioUrl: messageObj.audioUrl || "",
          overall,
          phonetic: this.extractWordPhonetic(wordMessage),
          phonemes: this.parseWordPhonemes(wordMessage, overall),
          raw: wordMessage,
        };
        this.upsertWordResult(result);
      });
    },
    getResultWords(messageObj) {
      const words = messageObj && messageObj.result && messageObj.result.words;
      if (!words) return [];
      return Array.isArray(words) ? words : [words];
    },
    findSdkWordResult(words, currentWord, index) {
      const normalized = currentWord.normalized;
      return (
        words.find((item) => {
          const text = String(
            item.word || item.text || item.content || item.refText || ""
          )
            .toLowerCase()
            .replace(/^[^a-z]+|[^a-z]+$/g, "");
          return text && text === normalized;
        }) ||
        words[index] ||
        {}
      );
    },
    openSentenceWordResult(index) {
      this.selectedWordIndex = index;
      this.activeRecordKind = "word";
      this.recordStatus = "done";
      this.practiceScreen = "wordResult";
      this.wordResultTab = "error";
    },
    backToSentence() {
      this.clearRecordTimer();
      this.practiceScreen = this.currentResult ? "sentenceResult" : "sentence";
      this.activeRecordKind = "sentence";
      this.recordStatus = this.currentResult ? "done" : "idle";
    },
    retryLine() {
      this.practiceScreen = "sentence";
      this.recordStatus = "idle";
      this.activeRecordKind = "sentence";
      this.autoPlayCurrentReference();
    },
    retryWord() {
      this.practiceScreen = "wordRecord";
      this.activeRecordKind = "word";
      this.recordStatus = "idle";
      this.autoPlayCurrentReference();
    },
    changeWord() {
      const words = this.currentLineWords;
      if (!words.length) return;
      this.selectedWordIndex = (this.selectedWordIndex + 1) % words.length;
      this.practiceScreen = "wordRecord";
      this.activeRecordKind = "word";
      this.recordStatus = this.hasWordResult(this.currentWord.normalized)
        ? "done"
        : "idle";
      this.autoPlayCurrentReference();
    },
    goNextLine() {
      if (!this.currentResult) return;

      if (!this.isLastLineInSegment) {
        this.currentLineIndex += 1;
        this.selectedWordIndex = 0;
        this.practiceScreen = "sentence";
        this.activeRecordKind = "sentence";
        this.recordStatus = "idle";
        this.autoPlayCurrentReference();
        return;
      }

      this.showSummary();
    },
    startRequiredWords() {
      this.requiredWordMode = true;
      this.requiredWordIndex = 0;
      this.mode = "practice";
      this.practiceScreen = "wordRecord";
      this.activeRecordKind = "word";
      this.recordStatus = "idle";
      this.autoPlayCurrentReference();
    },
    startSentences() {
      this.requiredWordMode = false;
      this.currentLineIndex = 0;
      this.selectedWordIndex = 0;
      this.mode = "practice";
      this.practiceScreen = "sentence";
      this.activeRecordKind = "sentence";
      this.recordStatus = "idle";
      this.autoPlayCurrentReference();
    },
    goNextRequiredWord() {
      if (!this.currentWordResult || !this.currentWordResult.overall) return;
      if (!this.isLastRequiredWord) {
        this.requiredWordIndex += 1;
        this.practiceScreen = "wordRecord";
        this.activeRecordKind = "word";
        this.recordStatus = "idle";
        this.autoPlayCurrentReference();
        return;
      }
      if (this.hasSentences) {
        this.startSentences();
        return;
      }
      this.showSummary();
    },
    handleTopBack() {
      if (this.showSentenceBack) {
        this.backToSentence();
        return;
      }
      this.backToLessonSelect();
    },
    backToLessonSelect() {
      if (this.recordStatus === "recording" || this.recordStatus === "scoring") {
        return;
      }
      this.$router.push(`/a/${this.activityId}/lessons`);
    },
    goNextLesson() {
      this.$router.push(`/a/${this.activityId}/lessons`);
    },
    async showSummary() {
      this.mode = "summary";
      const items = this.buildLessonScoreItems();
      if (!items.length) {
        return;
      }

      try {
        const res = await completeActivityLesson(this.activityId, Number(this.$route.params.lessonIndex), {
          items,
        });
        this.lessonGrade = (res.data && res.data.grade) || "";
      } catch (error) {
        localStorage.setItem(
          `activityLessonPending:${this.activityId}:${this.$route.params.lessonIndex}`,
          JSON.stringify({
            activityId: this.activityId,
            lessonIndex: Number(this.$route.params.lessonIndex),
            items,
          })
        );
      }
    },
    buildLessonScoreItems() {
      const sentenceItems = this.results.map((result) => ({
        type: "sentence",
        id: result.lineId,
        score: result.overall,
      }));
      const wordItems = this.requiredWords.map((word, index) => {
        const key = `required:${word.id || index}`;
        const result = this.wordResults.find((item) => item.key === key);
        return result
          ? { type: "word", id: String(word.id), score: result.overall }
          : null;
      }).filter(Boolean);
      return sentenceItems.concat(wordItems);
    },
    replayRecord(recordId) {
      if (!this.recorder || !recordId) return;
      this.recorder.startReplay({
        recordId,
        onStart: () => {},
        onStop: () => {},
      });
    },
    playWordRecording() {
      const result = this.currentWordResult;
      if (!result || (!result.audioUrl && !result.recordId)) return;

      if (!result.audioUrl) {
        this.replayRecord(result.recordId);
        return;
      }

      this.playReferenceAudio(result.audioUrl, () => {
        this.replayRecord(result.recordId);
      });
    },
    autoPlayCurrentReference() {
      this.$nextTick(() => {
        if (this.practiceScreen === "wordRecord") {
          this.playReferenceAudio(this.currentWordAudioUrl);
          return;
        }
        if (this.practiceScreen === "sentence") {
          this.playReferenceAudio(this.currentLineAudioUrl);
        }
      });
    },
    playReferenceAudio(url, onError) {
      const audioUrl = String(url || "").trim();
      if (!audioUrl) return;
      this.stopWordAudio();
      this.wordAudioPlayer = new Audio(audioUrl);
      const playTask = this.wordAudioPlayer.play();
      if (playTask && playTask.catch) {
        playTask.catch(() => {
          if (typeof onError === "function") onError();
        });
      }
    },
    stopWordAudio() {
      if (!this.wordAudioPlayer) return;
      this.wordAudioPlayer.pause();
      this.wordAudioPlayer = null;
    },
    restartPractice() {
      this.sessionId = `story-session-${Date.now()}`;
      this.startedAt = new Date().toISOString();
      this.currentLineIndex = 0;
      this.selectedWordIndex = 0;
      this.results = [];
      this.wordResults = [];
      this.requiredWordMode = false;
      this.requiredWordIndex = 0;
      this.lessonGrade = "";
      this.recordStatus = "idle";
      this.mode = "practice";
      if (this.hasRequiredWords) {
        this.startRequiredWords();
      } else {
        this.startSentences();
      }
    },
  },
};
</script>

<style scoped>
.story-practice-page {
  min-height: 100vh;
  background: #fff;
  color: #050505;
  overflow: hidden;
}

.recorder-host {
  position: fixed;
  width: 1px;
  height: 1px;
  left: -10px;
  top: -10px;
  opacity: 0;
  pointer-events: none;
}

.center-state,
.practice-stage,
.summary-stage {
  min-height: 100vh;
}

.center-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  color: #333;
}

.practice-stage {
  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 47px 26px 42px;
}

.page-title {
  text-align: center;
  font-size: 16px;
  line-height: 1.2;
  font-weight: 800;
}

.skip-segment-btn {
  position: fixed;
  top: calc(12px + env(safe-area-inset-top, 0px));
  left: 16px;
  z-index: 20;
  border: none;
  background: rgba(255, 255, 255, 0.92);
  color: #333;
  font-size: 14px;
  padding: 8px 14px;
  border-radius: 20px;
  cursor: pointer;
}

.lesson-badge {
  position: fixed;
  top: calc(12px + env(safe-area-inset-top, 0px));
  right: 16px;
  z-index: 20;
  background: rgba(0, 181, 173, 0.12);
  color: #00b5ad;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 20px;
}

.practice-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.sentence-main {
  padding-top: 104px;
}

.word-main {
  padding-top: 104px;
}

.hint-text {
  margin: 0;
  color: #050505;
  font-size: 17px;
  line-height: 1.45;
  font-weight: 700;
}

.sentence-text {
  margin-top: 66px;
  max-width: 100%;
  color: #050505;
  font-size: 39px;
  line-height: 1.25;
  font-weight: 400;
}

.target-word {
  margin: 66px 0 0;
  color: #050505;
  font-size: 39px;
  line-height: 1.25;
  font-weight: 400;
}

.word-phonetic-hint {
  margin: 10px 0 0;
  color: #888;
  font-size: 16px;
  line-height: 1.3;
}

.play-ref-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 22px;
  min-width: 108px;
  height: 36px;
  padding: 0 18px;
  border: none;
  border-radius: 999px;
  background: #e8f7f6;
  color: #12867f;
  font-size: 15px;
  font-weight: 700;
}

.line-result {
  margin-top: 34px;
}

.score-label {
  margin: 0;
  color: #666;
  font-size: 12px;
}

.score-number {
  margin: 4px 0 0;
  color: #30cf78;
  font-size: 34px;
  font-weight: 700;
}

.score-number span {
  font-size: 14px;
}

.practice-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 207px;
}

.swap-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-width: 107px;
  height: 29px;
  margin-bottom: 36px;
  padding: 0 18px;
  border: none;
  border-radius: 999px;
  background: #e7f8ef;
  color: #1dc46d;
  font-size: 16px;
  font-weight: 800;
}

.swap-icon {
  width: 20px;
  height: 20px;
  display: block;
  flex: 0 0 auto;
}

.mic-btn {
  width: 82px;
  height: 82px;
  border: none;
  border-radius: 50%;
  background: transparent url("../assets/record-mic.png") center / contain
    no-repeat;
  position: relative;
}

.mic-btn:before {
  display: none;
}

.mic-btn:after {
  display: none;
}

.mic-btn.recording {
  transform: scale(0.96);
}

.record-hint {
  margin: 25px 0 0;
  color: #666;
  font-size: 14px;
  line-height: 1.3;
  font-weight: 700;
}

.recorder-status {
  margin: 8px 0 0;
  max-width: 280px;
  color: #999;
  font-size: 11px;
  line-height: 1.5;
  text-align: center;
}

.line-actions,
.summary-actions {
  display: flex;
  gap: 18px;
  margin-top: 18px;
}

.pill-btn {
  min-width: 108px;
  height: 36px;
  padding: 0 18px;
  border: none;
  border-radius: 999px;
  background: #30cf78;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
}

.pill-btn.light {
  background: #e7f8ef;
  color: #26c76f;
}

.pill-btn.wide {
  min-width: 132px;
  height: 50px;
  font-size: 17px;
}

.sentence-result-stage {
  min-height: calc(100vh - 89px);
  display: flex;
  flex-direction: column;
  margin: -47px -26px -42px;
  background: #fff;
}

.sentence-score-panel {
  box-sizing: border-box;
  min-height: 328px;
  padding: 48px 34px 28px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.score-ring {
  width: 138px;
  height: 138px;
  border-radius: 50%;
  padding: 10px;
  transform: rotate(-132deg);
}

.score-ring-inner {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #fff;
  display: flex;
  flex-wrap: wrap;
  align-content: center;
  justify-content: center;
  transform: rotate(132deg);
  color: #050505;
}

.score-ring-inner strong {
  font-size: 47px;
  line-height: 1;
  font-weight: 800;
}

.score-ring-inner span {
  align-self: flex-end;
  margin: 0 0 6px 4px;
  color: #555;
  font-size: 18px;
  line-height: 1;
}

.score-ring-inner p {
  flex: 0 0 100%;
  margin: 15px 0 0;
  color: #666;
  font-size: 20px;
  line-height: 1;
  text-align: center;
}

.score-breakdown {
  width: 100%;
  max-width: 318px;
  margin-top: 36px;
}

.score-meter {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) 48px 22px;
  align-items: center;
  column-gap: 8px;
  min-height: 28px;
}

.meter-label {
  color: #050505;
  font-size: 19px;
  line-height: 1;
  font-weight: 800;
  white-space: nowrap;
}

.meter-track {
  height: 11px;
  overflow: hidden;
  background: #edf0f4;
}

.meter-track i {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #66eda0 0%, #9cf878 100%);
}

.score-meter strong,
.speed-meter strong {
  color: #050505;
  font-size: 28px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: 0;
}

.meter-unit,
.speed-unit {
  color: #b8bac7;
  font-size: 25px;
  line-height: 1;
}

.speed-meter {
  display: flex;
  align-items: center;
  min-height: 31px;
  gap: 9px;
}

.speed-meter .meter-label {
  margin-right: 3px;
}

.speed-unit {
  font-size: 19px;
}

.sentence-word-panel {
  box-sizing: border-box;
  min-height: 177px;
  padding: 30px 38px;
  background: #f5f5f8;
}

.sentence-word-panel p {
  margin: 0;
  color: #b8bac7;
  font-size: 18px;
  line-height: 1.3;
  font-weight: 700;
}

.result-word-list {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  margin-top: 24px;
}

.result-word-chip {
  min-width: 42px;
  height: 48px;
  padding: 0 12px;
  border: none;
  border-radius: 8px;
  background: #fff;
  color: #30cf78;
  font-size: 33px;
  line-height: 1;
  font-weight: 400;
}

.sentence-result-actions {
  display: flex;
  justify-content: center;
  gap: 27px;
  margin-top: auto;
  padding: 38px 28px 57px;
}

.word-result-stage {
  min-height: calc(100vh - 89px);
  display: flex;
  flex-direction: column;
  position: relative;
  padding-top: 2px;
}

.summary-score p {
  margin: 0;
  font-size: 17px;
  line-height: 1.2;
  font-weight: 700;
}

.summary-score strong {
  font-size: 44px;
  line-height: 1.2;
  font-weight: 400;
}

.summary-score span {
  font-size: 18px;
}

.word-score {
  margin-top: 0;
}

.word-score p {
  font-size: 12px;
}

.word-score strong {
  font-size: 34px;
}

.word-score span {
  font-size: 14px;
}

.play-record-btn {
  position: absolute;
  top: 4px;
  right: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 108px;
  height: 30px;
  padding: 0 14px;
  border: none;
  border-radius: 999px;
  background: #e7f8ef;
  color: #30cf78;
  font-size: 14px;
  line-height: 1;
  font-weight: 800;
}

.play-record-icon {
  width: 0;
  height: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 8px solid #30cf78;
}

.word-result-hero {
  margin-top: 36px;
  text-align: center;
}

.word-result-hero h1 {
  margin: 0;
  color: #30cf78;
  font-size: 39px;
  line-height: 1.25;
  font-weight: 400;
}

.word-result-hero h1.weak {
  color: #d0021b;
}

.word-phonetic-text {
  margin: 10px 0 18px;
  color: #050505;
  font-size: 20px;
  line-height: 1.2;
}

.phonetic-score-high {
  color: #30cf78;
}

.phonetic-score-low {
  color: #d0021b;
}

.error-tabs {
  display: inline-grid;
  grid-template-columns: 1fr 1fr;
  width: 180px;
  height: 30px;
  overflow: hidden;
  border: 1px solid #30cf78;
  border-radius: 999px;
}

.error-tabs button {
  border: none;
  background: #fff;
  color: #555;
  font-size: 14px;
  font-weight: 800;
}

.error-tabs button.active {
  background: #30cf78;
  color: #fff;
}

.phoneme-table {
  margin: 28px auto 0;
  width: 100%;
  max-width: 288px;
  overflow: hidden;
  border-radius: 6px 6px 0 0;
}

.phoneme-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
  min-height: 28px;
  background: #fff;
  color: #555;
  font-size: 14px;
  text-align: center;
}

.phoneme-row.header {
  background: #eceeef;
}

.phoneme-row:not(.header):nth-child(odd) {
  background: #eceeef;
}

.phoneme-row.header {
  min-height: 28px;
  color: #8d8d8d;
  font-size: 12px;
  font-weight: 800;
}

.phoneme-row strong {
  color: #050505;
  font-size: 14px;
  font-weight: 700;
}

.phoneme-row.weak span:nth-child(2),
.phoneme-row.weak strong {
  color: #f5a623;
}

.word-result-actions {
  display: flex;
  justify-content: center;
  gap: 27px;
  margin-top: auto;
  padding: 28px 7px 0;
}

.summary-stage {
  box-sizing: border-box;
  padding: 46px 30px 34px;
  display: flex;
  flex-direction: column;
}

.summary-table {
  flex: 1;
  overflow-y: auto;
  margin-top: 12px;
}

.summary-row {
  display: grid;
  grid-template-columns: 1fr 52px 54px;
  gap: 8px;
  align-items: center;
  min-height: 34px;
  padding: 6px 10px;
  margin-bottom: 6px;
  background: #f2f3f5;
  color: #333;
  font-size: 12px;
}

.summary-row.header {
  color: #999;
  font-weight: 600;
}

.summary-row.weak {
  background: #fff2f2;
}

.summary-row button {
  border: 1px solid #30cf78;
  border-radius: 999px;
  background: #fff;
  color: #30cf78;
  font-size: 11px;
  height: 24px;
}

.summary-actions {
  justify-content: center;
}

.scene-switch-enter-active {
  transition: opacity 260ms ease, transform 260ms ease;
}

.scene-switch-leave-active {
  transition: opacity 180ms ease, transform 180ms ease;
}

.scene-switch-enter {
  opacity: 0;
  transform: translateY(12px);
}

.scene-switch-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

button {
  cursor: pointer;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>



// WEBPACK FOOTER //
// src/views/StoryPractice.vue